// Encapsulates the whole capture-session lifecycle (create recording -> open bridge WS -> pipe
// browser PCM in -> surface incoming segments + a running cost estimate -> stop/persist) for
// BOTH batch and streaming capture (docs/specs/phase1-2.md §2) so RecordScreen stays thin and
// the flow is testable with injected fakes. `mode: 'batch'` durably ingests PCM with no live
// captions (the backend assembles a playable master and triggers batch transcription on stop —
// docs/specs/phase1-1.md §4); `mode: 'streaming'` is the existing live-caption behavior,
// unchanged, now also durably captured. See docs/specs/webstream-2.md §4 and
// docs/web-streaming-client-handoff.md §3 for the full write-up (in particular the
// CreateRecordingRequest gotcha below).
//
// NEVER log segment text or the token anywhere in this module.
import { useCallback, useEffect, useRef, useState } from 'react';
import { estimateTranscriptionCostUsd } from '@scribe/core';
import { ScribeApiError } from '@scribe/core/client';
import { classifyCaptureError } from '@scribe/core/errors';
import {
  openStreamingSession,
  StreamingError,
  type StreamingErrorCode,
  type StreamingSegment,
  type StreamingSession,
} from '@scribe/core/streaming';
import type { EngineChoice, TranscriptionMode } from '@scribe/core/types';
import { startPcmCapture, type PcmCaptureHandle } from '../audio/pcmCapture';
import { useApi } from '../lib/ApiContext';
import { COMBINED_LANGUAGE, COMBINED_LANGUAGE_OPTIONS } from '../lib/languages';
import { isMock } from '../lib/mock';
import { isCaptureErrorCode, type RecordErrorCode } from '../lib/streamingErrors';

const TICK_MS = 250;

export type StreamingStatus = 'idle' | 'connecting' | 'live' | 'stopping' | 'done' | 'error';

export interface StreamingSessionState {
  status: StreamingStatus;
  recordingId: string | null;
  /** `is_partial === false` segments, in arrival order. Always empty for `mode: 'batch'`
   * (the bridge sends no live-caption JSON in that mode). */
  finalized: StreamingSegment[];
  /** Latest `is_partial === true` segment, or null. Always null for `mode: 'batch'`. */
  partial: StreamingSegment | null;
  elapsedMs: number;
  costUsd: number;
  /** Set on failure — WEBSTREAM-3 maps this to user-facing copy. Wider than
   * `StreamingErrorCode` because not every failure the record screen shows comes off the wire
   * (see `bridge_not_configured`). */
  error: RecordErrorCode | null;
  /** False in mock mode or when no bridge URL is configured. */
  available: boolean;
  /** True while an abnormal close is being auto-retried (docs/specs/phase1-2.md §1) — the
   * client is reopening the bridge WS and replaying un-acked audio, not lost/failed. */
  reconnecting: boolean;
  /** True once the bridge has acked at least one durably-ingested chunk — i.e. some audio
   * really is saved. Gates the "nothing is lost" reassurance on the error banner, which used
   * to be unconditional and therefore lied whenever a session failed before (or instead of)
   * capturing anything. Acks, not `status === 'live'`: reaching `live` only proves the mic
   * opened, and the first ~5s of a session are genuinely un-acked. */
  captured: boolean;
  /** Client-side mirror of the bridge's `run_batch_pass = had_batch_window or was_resumed or
   * language == "multi"` (`backend/src/scribe/streaming/bridge.py`): true when the backend will run a batch pass
   * over the whole file on stop, so the recording lands `queued`/`transcribing` and the live
   * captions the user just watched are about to be superseded rather than persisted as-is. A
   * false positive only costs a redundant provisional-caption render. */
  batchPassExpected: boolean;
  /** ERRORS-1: the microphone went away mid-session (a track `ended`). The session is NOT
   * torn down — Stop still finalizes whatever was captured — the record screen just says so,
   * instead of showing a healthy "Recording…" while nothing is being heard. */
  deviceLost: boolean;
}

export interface StartMeta {
  title: string;
  folderId: string | null;
  tags: string[];
  /**
   * VOCAB-10: a published Vocabulary's name, picked on RecordScreen before Start — null/
   * omitted means "None" (no vocabulary bias). Forwarded into `createRecording`'s
   * `job_spec.vocabulary_name`; `streaming/bridge.py` then merges the recording's stored
   * job_spec with the control message's `{mode}` on bind, so this reaches
   * `AwsStreamingTranscriber`/AWS Transcribe batch either way without needing to also be
   * repeated in the bridge's own job_spec below.
   */
  vocabularyName?: string | null;
  /** §2.7: the transcription language the user chose (`en-US`, `es-US`, or `multi`). Always
   *  sent, even when it is the default — see `useTranscriptionPrefs.buildJobSpec` for why an
   *  omitted language is worse than an explicit one even though the server treats them the
   *  same. */
  language?: string | null;
  /** AI-3: whether `title` is a generated fallback rather than user-typed — forwarded as
   * `title_is_default` so the AI auto-title pass (AI-1/AI-2) knows it's free to replace it. */
  titleIsDefault: boolean;
  /** AI-3: per-recording opt-out for the automatic post-transcription AI summary — default
   * checked (on) in RecordScreen's UI; unchecking sends `job_spec.auto_summary: false`. */
  autoSummary: boolean;
  /** WHISPER-1: per-recording engine for the batch pass; absent/"aws" = the default. */
  engine?: EngineChoice;
}

export interface UseStreamingSession extends StreamingSessionState {
  /** `mode: 'batch'` durably ingests PCM with no live captions; `mode: 'streaming'` is the
   * existing live-caption path, now also durably captured (docs/specs/phase1-2.md §2). */
  start(meta: StartMeta, mode: TranscriptionMode): Promise<void>;
  /** Resolves the recordingId to navigate to, or null on error. */
  stop(): Promise<string | null>;
  /** Current live-STT mode (SWAP-2, docs/specs/phase3-2.md §2) — set by `start`'s `mode` arg,
   * then flippable mid-session via `setMode` while `connecting`/`live`/`stopping`. */
  mode: TranscriptionMode;
  /** Flips the live-STT mode mid-session: delegates to `session.setMode` (a no-op before a
   * session exists / after it's closed) and updates local state immediately so the UI
   * (captions, cost indicator) reflects it without waiting on a round trip. `reason: 'auto'`
   * marks a swap made by `useLiveAutoSwitch` (LIVE-SAVE-1) — metering only. */
  setMode(mode: TranscriptionMode, reason?: 'auto'): void;
}

// Injection seams default to the real impls; tests pass fakes.
export interface StreamingSessionDeps {
  openSession?: typeof openStreamingSession;
  startCapture?: typeof startPcmCapture;
}

/** HTTP status -> the equivalent bridge close-code meaning, so a REST rejection and a WS
 * rejection of the SAME cause read identically to the user. */
const API_STATUS_CODES: Record<number, StreamingErrorCode> = {
  400: 'bad_request',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
};

function toErrorCode(err: unknown): RecordErrorCode {
  // ERRORS-1: a mic-permission denial / missing device / unsupported browser used to fall
  // through to 'network' below, telling the user to check their wifi. Name it.
  const capture = classifyCaptureError(err);
  if (capture && isCaptureErrorCode(capture)) return capture;
  if (err instanceof StreamingError) return err.code;
  // `start()` calls `createRecording` BEFORE opening the socket, so a bind-time refusal can
  // arrive as an HTTP error rather than a close code. Collapsing those to 'network' told the
  // user "Connection lost. Check your network" when the real cause was a monthly cost cap
  // (ADMIN-4 returns 403 `budget_exceeded` from POST /recordings) — the bridge's own 4402
  // path was mapped correctly all along, so only the REST path lied (T9, M10 dev validation
  // 2026-08-06). Prefer the server's own error code, then fall back to the status.
  if (err instanceof ScribeApiError) {
    if (err.code === 'budget_exceeded') return 'budget_exceeded';
    return API_STATUS_CODES[err.status] ?? 'network';
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  return 'network';
}

export function useStreamingSession(deps: StreamingSessionDeps = {}): UseStreamingSession {
  const { client, getToken, streamingBridgeUrl } = useApi();
  const available = !isMock() && !!streamingBridgeUrl;

  const [status, setStatus] = useState<StreamingStatus>('idle');
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [finalized, setFinalized] = useState<StreamingSegment[]>([]);
  const [partial, setPartial] = useState<StreamingSegment | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<RecordErrorCode | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [deviceLost, setDeviceLost] = useState(false);
  // Mirrors `captured` so the ack callback (~every 5s, both modes) can early-return instead of
  // queueing a state update per ack.
  const capturedRef = useRef(false);
  // The two halves of the bridge's `run_batch_pass` rule, tracked as state (a ref alone would
  // never re-render the caption block that reads `batchPassExpected`).
  // `hadBatchWindow` mirrors the bridge's "an audio frame arrived while STT was off": it flips
  // only when a frame is actually SENT while `modeRef` is 'batch' (the bridge applies
  // `set_mode` before any later frame on the same socket). Flipping on the toggle itself would
  // count a batch start switched to live before its first frame — the bridge runs no batch
  // pass for that session, but `useLiveAutoSwitch` would treat it as already-mixed and
  // auto-switch it, inducing exactly the full-file pass the pure-live policy avoids. The ref
  // keeps the per-frame check free of re-renders.
  const [hadBatchWindow, setHadBatchWindow] = useState(false);
  const hadBatchWindowRef = useRef(false);
  const [wasResumed, setWasResumed] = useState(false);
  // The bridge's third reason: a multi-language recording always gets a batch pass, because
  // live language identification is weaker on code-switching audio than batch over the file.
  const [isMultiLanguage, setIsMultiLanguage] = useState(false);
  // SWAP-2 (docs/specs/phase3-2.md §2): the live-STT mode, mutable mid-session via setMode.
  const [mode, setModeState] = useState<TranscriptionMode>('streaming');
  // Mirrors `mode` for the timer tick closure below (a stale closure over `mode` state would
  // never see a mid-session toggle). Kept in sync everywhere `mode` state is set.
  const modeRef = useRef<TranscriptionMode>('streaming');
  // Approximate cumulative milliseconds spent in `mode === 'streaming'`, accumulated one
  // TICK_MS tick at a time (see the interval below) — NOT the whole session's elapsed time.
  // Approximation: a toggle lands mid-tick, so up to one TICK_MS (250ms) of a window can be
  // mis-attributed to whichever mode was current at the tick boundary. Good enough for an
  // indicative cost readout; exact per-window accounting is out of scope (see spec §6).
  const [liveElapsedMs, setLiveElapsedMs] = useState(0);

  const sessionRef = useRef<StreamingSession | null>(null);
  const captureRef = useRef<PcmCaptureHandle | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef(0);
  // Guards against stop() racing an in-flight start(): flipped true by stop() when it fires
  // while status is still 'connecting' (refs aren't populated yet, so stop() can't tear
  // anything down itself). start() checks this after every await and unwinds whatever it just
  // created instead of letting a stale session/capture pair go live unattended.
  const abortedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cleanup on unmount: stop capture + close the session so a navigated-away tab doesn't
  // leak the mic or socket.
  useEffect(
    () => () => {
      clearTimer();
      void captureRef.current?.stop();
      sessionRef.current?.close();
    },
    [clearTimer],
  );

  const start = useCallback(
    async (meta: StartMeta, mode: TranscriptionMode) => {
      if (!available || !streamingBridgeUrl) {
        // Not a network failure: this build has no capture bridge, so nothing was ever
        // attempted. Its own code so the banner can say so (and skip the "already saved"
        // reassurance) — see `RecordErrorCode`.
        setError('bridge_not_configured');
        setStatus('error');
        return;
      }

      abortedRef.current = false;
      setError(null);
      setFinalized([]);
      setPartial(null);
      setElapsedMs(0);
      setRecordingId(null);
      setReconnecting(false);
      setStatus('connecting');
      capturedRef.current = false;
      setCaptured(false);
      setDeviceLost(false);
      hadBatchWindowRef.current = false;
      setHadBatchWindow(false);
      setWasResumed(false);
      setIsMultiLanguage(meta.language === COMBINED_LANGUAGE);
      modeRef.current = mode;
      setModeState(mode);
      setLiveElapsedMs(0);

      // Tracked locally (in addition to the refs) so the abort-guard checks below and the
      // outer catch can tear down whatever this call created even before the refs are
      // populated — see FINDING 1/2 in the streaming-client review.
      let session: StreamingSession | null = null;
      let capture: PcmCaptureHandle | null = null;

      try {
        // Gotcha (handoff §3): CreateRecordingRequest requires content_type + part_count and
        // mints an S3 multipart presign the streaming path never uses — pass throwaway values
        // and ignore response.upload entirely.
        // TODO(WEBSTREAM follow-up): streaming-aware create that skips the presign.
        const jobSpecFields = {
          ...(meta.language ? { language: meta.language } : {}),
          // Same as `useTranscriptionPrefs.buildJobSpec`: record the candidates explicitly
          // rather than leaning on the server default.
          ...(meta.language === COMBINED_LANGUAGE
            ? { language_options: [...COMBINED_LANGUAGE_OPTIONS] }
            : {}),
          ...(meta.vocabularyName ? { vocabulary_name: meta.vocabularyName } : {}),
          // Omitted for the default, so the AWS request shape is unchanged.
          ...(meta.engine && meta.engine !== 'aws' ? { engine: meta.engine } : {}),
        };
        const jobSpec = Object.keys(jobSpecFields).length > 0 ? jobSpecFields : undefined;
        const response = await client.createRecording({
          title: meta.title,
          folder_id: meta.folderId,
          source: 'upload',
          content_type: 'audio/pcm',
          part_count: 1,
          tags: meta.tags,
          title_is_default: meta.titleIsDefault,
          // §2.7: send whatever was actually chosen, and omit `job_spec` entirely when
          // NOTHING was — an empty object would be a new request shape carrying no
          // information. RecordScreen always supplies a language, so in practice this is
          // always sent from there; the omission path covers a caller that supplies neither
          // (and preserves the pre-VOCAB-10 shape for it exactly).
          ...(jobSpec ? { job_spec: jobSpec } : {}),
        });
        const newRecordingId = response.recording.id;
        setRecordingId(newRecordingId);

        if (abortedRef.current) {
          // stop() already fired while we were still 'connecting' — nothing was created yet.
          return;
        }

        // Token-expiry handling (WEBSTREAM-3, docs/specs/webstream-3.md §5): `getToken` is
        // MSAL's `acquireTokenSilent`, which transparently refreshes a near-expiry token, and
        // `openStreamingSession` calls it fresh on every connect (including every auto-reconnect
        // attempt — docs/specs/phase1-2.md §1) — so every `start()` call (including a Restart
        // after a terminal 4401) picks up a current token. A *terminal* close (expired/revoked
        // auth, forbidden, not-found) still surfaces as `unauthorized`/etc. via the normal
        // close-code path below, with the only recovery being a fresh session (RecordScreen's
        // Restart) — only an *abnormal* close (transport drop, engine hiccup) is auto-retried
        // transparently, surfaced here as `reconnecting`.
        const openSession = deps.openSession ?? openStreamingSession;
        session = await openSession({
          bridgeUrl: streamingBridgeUrl,
          getToken,
          recordingId: newRecordingId,
          // AI-3: only send `auto_summary` on opt-out (absent = server default, per AI-1's
          // tri-state decision) — mirrors the vocabulary-name omission above.
          jobSpec: { mode, ...(meta.autoSummary ? {} : { auto_summary: false }) },
          onAck: (ackedBytes) => {
            if (ackedBytes > 0 && !capturedRef.current) {
              capturedRef.current = true;
              setCaptured(true);
            }
          },
          onReconnecting: (value) => {
            if (abortedRef.current) return;
            setReconnecting(value);
            // A resume is the bridge's other trigger for a batch pass — once this session has
            // reconnected at all, its stop will be batch-authoritative.
            if (value) setWasResumed(true);
          },
        });
        if (abortedRef.current) {
          // stop() fired while openSession() was in flight — close what it just opened before
          // anything (a render, the segments loop) can observe it via the ref.
          session.close();
          return;
        }
        sessionRef.current = session;

        // Finding 3 (belt-and-braces): `setMode` no-ops while `sessionRef` is still null (the
        // exact window this connection was just opening in), so a toggle during `connecting`
        // updates `modeRef`/local state but never reaches the session itself — desyncing the
        // UI (which already reflects the toggle) from what the bridge is actually doing.
        // `sessionRef` now exists: if a toggle landed before we got here (`modeRef.current`
        // no longer matches the `mode` this session was opened with), apply it now.
        if (modeRef.current !== mode) {
          session.setMode(modeRef.current);
        }

        const startCapture = deps.startCapture ?? startPcmCapture;
        capture = await startCapture({
          onFrame: (pcm) => {
            session!.sendAudio(pcm);
            if (modeRef.current === 'batch' && !hadBatchWindowRef.current) {
              hadBatchWindowRef.current = true;
              setHadBatchWindow(true);
            }
          },
          // A mid-session capture failure (device unplugged, worklet crash) must actually
          // surface: flip status to 'error' (RecordScreen's banner is gated on it) and tear
          // down the mic + bridge WS, rather than only setting `error` and silently going deaf
          // while the UI still shows a healthy 'live' session. (Review finding.)
          onDeviceLost: () => {
            if (!abortedRef.current) setDeviceLost(true);
          },
          onError: (err: unknown) => {
            if (abortedRef.current) return;
            setError(toErrorCode(err));
            setStatus('error');
            setReconnecting(false);
            clearTimer();
            void captureRef.current?.stop().catch(() => {});
            sessionRef.current?.close();
            sessionRef.current = null;
            captureRef.current = null;
          },
        });
        if (abortedRef.current) {
          // stop() fired while startCapture() was in flight — tear down the mic + session it
          // never got a chance to see in the refs.
          await capture.stop().catch(() => {});
          session.close();
          sessionRef.current = null;
          return;
        }
        captureRef.current = capture;

        setStatus('live');
        startedAtRef.current = Date.now();
        clearTimer();
        timerRef.current = setInterval(() => {
          setElapsedMs(Date.now() - startedAtRef.current);
          if (modeRef.current === 'streaming') {
            setLiveElapsedMs((prev) => prev + TICK_MS);
          }
        }, TICK_MS);

        // Consume segments for the lifetime of the session — not awaited here, this runs in
        // the background until the socket closes (normally on stop(), or a non-1000 close).
        void (async () => {
          try {
            for await (const seg of session.segments()) {
              if (seg.is_partial) {
                setPartial(seg);
              } else {
                setFinalized((prev) => [...prev, seg]);
                setPartial(null);
              }
            }
          } catch (err) {
            setError(toErrorCode(err));
            setStatus('error');
            setReconnecting(false);
            clearTimer();
            await captureRef.current?.stop();
          }
        })();
      } catch (err) {
        // Tear down anything this call already created before surfacing an error — otherwise
        // a mic-permission denial (or missing worklet) after openStreamingSession succeeded
        // leaves the bridge WS (and its server-side AWS Transcribe resources) dangling.
        if (capture) {
          await capture.stop().catch(() => {});
        }
        if (session) {
          session.close();
        }
        sessionRef.current = null;
        captureRef.current = null;
        clearTimer();
        // An abort (stop() during 'connecting') isn't an error — stop() already moved status
        // to 'idle'; don't clobber it.
        if (!abortedRef.current) {
          setError(toErrorCode(err));
          setStatus('error');
        }
      }
    },
    [available, streamingBridgeUrl, client, getToken, deps.openSession, deps.startCapture, clearTimer],
  );

  const stop = useCallback(async (): Promise<string | null> => {
    // The segments loop above already moved us to 'error' (and stopped capture) on a
    // non-1000 close — nothing left to negotiate with an already-dead session.
    if (status === 'error') return null;

    // Flip the abort guard before anything else so an in-flight start() (see FINDING 1) sees
    // it on its very next await-check, whichever branch below we take.
    abortedRef.current = true;

    if (status === 'connecting') {
      // start() hasn't reached 'live' yet — the refs may be empty, partially populated, or
      // fully populated depending on exactly where start() currently is. Tear down whatever
      // is already visible here; start()'s own abort checks handle whatever it creates after
      // this point (belt-and-braces, and idempotent to double-close).
      await captureRef.current?.stop().catch(() => {});
      captureRef.current = null;
      sessionRef.current?.close();
      sessionRef.current = null;
      clearTimer();
      setStatus('idle');
      return null;
    }

    setStatus('stopping');
    clearTimer();
    await captureRef.current?.stop();
    try {
      await sessionRef.current?.stop();
      setStatus('done');
      setReconnecting(false);
      return recordingId;
    } catch (err) {
      setError(toErrorCode(err));
      setStatus('error');
      setReconnecting(false);
      return null;
    }
  }, [status, clearTimer, recordingId]);

  // SWAP-2 (docs/specs/phase3-2.md §2): delegates to the live session's setMode (no-op if
  // there's no session yet/it's already closed) and updates local state immediately so the UI
  // (RecordScreen's toggle, LiveCaptions visibility, the cost indicator) reflects the flip
  // without waiting on a round trip.
  const setMode = useCallback((newMode: TranscriptionMode, reason?: 'auto') => {
    modeRef.current = newMode;
    setModeState(newMode);
    // Live STT going off mid-session starts the bridge's "batch window" — but it only counts
    // once a frame is sent in it (see `hadBatchWindow` above / `onFrame` in start()).
    if (reason) sessionRef.current?.setMode(newMode, reason);
    else sessionRef.current?.setMode(newMode);
  }, []);

  // Streaming rate applies only to time actually spent in `mode === 'streaming'`
  // (`liveElapsedMs`), not the whole session — a mostly-batch session shouldn't show a
  // full-session streaming cost (spec §2). Approximate, see `liveElapsedMs`'s comment above.
  const costUsd = estimateTranscriptionCostUsd('streaming', liveElapsedMs / 1000);

  return {
    status,
    recordingId,
    finalized,
    partial,
    elapsedMs,
    costUsd,
    error,
    available,
    reconnecting,
    captured,
    deviceLost,
    batchPassExpected: hadBatchWindow || wasResumed || isMultiLanguage,
    mode,
    start,
    stop,
    setMode,
  };
}
